import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useToast } from '../components/Toast';

/* ── Types ─────────────────────────────────────────────────────────── */

interface LeaveBalance {
  EmployeeUpn: string;
  DisplayName: string;
  Department: string | null;
  AnnualDays: number;
  UsedDays: number;
  Adjustment: number;
  RemainingDays: number;
}

interface LeaveRequest {
  RequestId: number;
  EmployeeUpn: string;
  DisplayName: string;
  StartDate: string;
  EndDate: string;
  LeaveType: string;
  BusinessDays: number;
  Reason: string | null;
  Status: string;
  ReviewedBy: string | null;
  ReviewedAt: string | null;
  CreatedAt: string;
  AttachmentCount?: number;
  CanReview?: boolean;
}

interface LeaveAttachment {
  AttachmentId: number;
  RequestId: number;
  FileName: string;
  ContentType: string;
  FileSize: number;
  CreatedAt: string | null;
}

interface PublicHoliday {
  HolidayId: number;
  Name: string;
  HolidayDate: string;
  CreatedAt: string;
}

const leaveTypes = [
  { value: 'annual', label: 'Annual Leave' },
  { value: 'sick', label: 'Sick Leave' },
  { value: 'family', label: 'Family Responsibility' },
  { value: 'study', label: 'Study Leave' },
  { value: 'unpaid', label: 'Unpaid Leave' },
  { value: 'other', label: 'Other' },
];

const statusColors: Record<string, string> = {
  pending: '#f59e0b',
  approved: '#10b981',
  rejected: '#ef4444',
  cancelled: '#6b7280',
};

const approvalStageLabel = (status: string): string => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'pending') return 'Pending manager review';
  if (normalized === 'approved') return 'Approved';
  if (normalized === 'rejected') return 'Declined';
  if (normalized === 'cancelled') return 'Cancelled';
  return status || '-';
};

const badgeStyle = (status: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600,
  color: '#fff',
  background: statusColors[status] || '#6b7280',
  textTransform: 'capitalize' as const,
});

/* ── Component ─────────────────────────────────────────────────────── */

export const LeaveManagementPage: React.FC = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { isAdmin, isManager } = useAuth();
  const { showToast } = useToast();

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [tab, setTab] = useState<'balances' | 'requests' | 'holidays'>('requests');

  // ── Balance editing ──
  const [editingUpn, setEditingUpn] = useState<string | null>(null);
  const [editAnnual, setEditAnnual] = useState<number | ''>('');
  const [editAdj, setEditAdj] = useState<number | ''>('');

  // ── New holiday ──
  const [newHolName, setNewHolName] = useState('');
  const [newHolDate, setNewHolDate] = useState('');

  // ── Filter ──
  const [search, setSearch] = useState('');
  const [reqFilter, setReqFilter] = useState<string>('all');
  const [attachmentViewRequestId, setAttachmentViewRequestId] = useState<number | null>(null);
  const [attachmentViewRows, setAttachmentViewRows] = useState<LeaveAttachment[]>([]);
  const [attachmentViewLoading, setAttachmentViewLoading] = useState(false);

  /* ── Queries ────────────────────────────────────────────────────── */

  const balancesQ = useQuery({
    queryKey: ['leave-balances', year],
    queryFn: () => api.get(`/leave/balances?year=${year}`) as Promise<LeaveBalance[]>,
    staleTime: 30000,
  });

  const requestsQ = useQuery({
    queryKey: ['leave-requests', year],
    queryFn: () => api.get(`/leave/requests?year=${year}`) as Promise<LeaveRequest[]>,
    staleTime: 15000,
  });

  const holidaysQ = useQuery({
    queryKey: ['leave-holidays', year],
    queryFn: () => api.get(`/leave/holidays?year=${year}`) as Promise<PublicHoliday[]>,
    staleTime: 60000,
  });

  /* ── Mutations ──────────────────────────────────────────────────── */

  const actionMut = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      api.put(`/leave/requests/${id}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave-requests'] });
      queryClient.invalidateQueries({ queryKey: ['leave-balances'] });
      showToast('Request updated', 'success');
    },
    onError: (err: any) => showToast(err?.body?.detail || 'Failed', 'error'),
  });

  const balanceMut = useMutation({
    mutationFn: ({ upn, annual, adj }: { upn: string; annual: number | null; adj: number | null }) =>
      api.put(`/leave/balances/${encodeURIComponent(upn)}?year=${year}${annual != null ? `&annual_days=${annual}` : ''}${adj != null ? `&adjustment=${adj}` : ''}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave-balances'] });
      showToast('Balance updated', 'success');
      setEditingUpn(null);
    },
    onError: (err: any) => showToast(err?.body?.detail || 'Failed', 'error'),
  });

  const addHolMut = useMutation({
    mutationFn: (payload: { name: string; holidayDate: string }) =>
      api.post('/leave/holidays', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave-holidays'] });
      showToast('Holiday added', 'success');
      setNewHolName('');
      setNewHolDate('');
    },
    onError: (err: any) => showToast(err?.body?.detail || 'Failed', 'error'),
  });

  const delHolMut = useMutation({
    mutationFn: (id: number) => api.del(`/leave/holidays/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave-holidays'] });
      showToast('Holiday removed', 'success');
    },
  });

  /* ── Derived ────────────────────────────────────────────────────── */

  const filteredBalances = useMemo(() => {
    if (!balancesQ.data) return [];
    if (!search.trim()) return balancesQ.data;
    const q = search.toLowerCase();
    return balancesQ.data.filter(
      b =>
        (b.DisplayName || '').toLowerCase().includes(q) ||
        (b.EmployeeUpn || '').toLowerCase().includes(q) ||
        (b.Department || '').toLowerCase().includes(q),
    );
  }, [balancesQ.data, search]);

  const filteredRequests = useMemo(() => {
    if (!requestsQ.data) return [];
    let rows = requestsQ.data;
    if (reqFilter !== 'all') rows = rows.filter(r => r.Status === reqFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(
        r =>
          (r.DisplayName || '').toLowerCase().includes(q) ||
          (r.EmployeeUpn || '').toLowerCase().includes(q),
      );
    }
    return rows;
  }, [requestsQ.data, reqFilter, search]);

  const canReviewRequests = useMemo(
    () => filteredRequests.some(r => !!r.CanReview) || isAdmin || isManager,
    [filteredRequests, isAdmin, isManager],
  );

  /* ── Render helpers ─────────────────────────────────────────────── */

  const tabBtn = (t: typeof tab, label: string) => (
    <button
      onClick={() => setTab(t)}
      style={{
        padding: '8px 20px',
        fontSize: 14,
        fontWeight: tab === t ? 700 : 400,
        borderBottom: tab === t ? '2px solid #2563eb' : '2px solid transparent',
        background: 'none',
        border: 'none',
        borderBottomWidth: 2,
        borderBottomStyle: 'solid',
        borderBottomColor: tab === t ? '#2563eb' : 'transparent',
        color: tab === t ? '#2563eb' : '#6b7280',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );

  const openBalanceEdit = (b: LeaveBalance) => {
    setEditingUpn(b.EmployeeUpn);
    setEditAnnual(b.AnnualDays);
    setEditAdj(b.Adjustment);
  };

  const saveBalance = () => {
    if (!editingUpn) return;
    balanceMut.mutate({
      upn: editingUpn,
      annual: editAnnual === '' ? null : editAnnual,
      adj: editAdj === '' ? null : editAdj,
    });
  };

  const openAttachments = async (requestId: number) => {
    setAttachmentViewRequestId(requestId);
    setAttachmentViewLoading(true);
    try {
      const rows = (await api.get(`/leave/requests/${requestId}/attachments`)) as LeaveAttachment[];
      setAttachmentViewRows(rows);
    } catch (err: any) {
      showToast(err?.body?.detail || err?.message || 'Failed to load attachments', 'error');
      setAttachmentViewRows([]);
    } finally {
      setAttachmentViewLoading(false);
    }
  };

  const toggleAttachments = async (requestId: number) => {
    if (attachmentViewRequestId === requestId) {
      setAttachmentViewRequestId(null);
      setAttachmentViewRows([]);
      setAttachmentViewLoading(false);
      return;
    }
    await openAttachments(requestId);
  };

  const downloadAttachment = async (attachment: LeaveAttachment) => {
    try {
      const blob = await api.getBlob(`/leave/attachments/${attachment.AttachmentId}/download`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.FileName || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      showToast(err?.body?.detail || err?.message || 'Failed to download attachment', 'error');
    }
  };

  /* ── Main render ────────────────────────────────────────────────── */

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>Leave Management</h2>
          <div style={{ marginTop: 4, color: '#6b7280', fontSize: 13 }}>
            Pending leave approvals appear in Requests with Approve / Reject actions.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Year:</label>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d1d5db' }}>
            {[currentYear - 1, currentYear, currentYear + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
        {tabBtn('balances', 'Leave Balances')}
        {tabBtn('requests', 'Leave Requests')}
        {tabBtn('holidays', 'Public Holidays')}
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="Search by name, email, or department..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '100%', maxWidth: 400, padding: '8px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 4 }}
        />
      </div>

      {/* ── Balances Tab ──────────────────────────────────────────── */}
      {tab === 'balances' && (
        <>
          {balancesQ.isLoading ? (
            <p style={{ color: '#6b7280' }}>Loading balances...</p>
          ) : balancesQ.error ? (
            <p style={{ color: '#ef4444' }}>Failed to load balances.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                    <th style={{ padding: '0.75rem' }}>Employee</th>
                    <th style={{ padding: '0.75rem' }}>Department</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Annual</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Used</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Adjustment</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 700 }}>Remaining</th>
                    <th style={{ padding: '0.75rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBalances.map(b => (
                    <tr key={b.EmployeeUpn} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem', fontWeight: 500 }}>{b.DisplayName}</td>
                      <td style={{ padding: '0.75rem', color: '#6b7280' }}>{b.Department || '-'}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{b.AnnualDays}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{b.UsedDays}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{b.Adjustment !== 0 ? (b.Adjustment > 0 ? `+${b.Adjustment}` : b.Adjustment) : '-'}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 700, color: b.RemainingDays < 0 ? '#ef4444' : b.RemainingDays <= 3 ? '#f59e0b' : '#10b981' }}>
                        {b.RemainingDays}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <button
                          onClick={() => openBalanceEdit(b)}
                          disabled={!isAdmin}
                          style={{ padding: '4px 12px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 4, background: 'white', cursor: 'pointer' }}
                        >
                          {isAdmin ? 'Edit' : 'View only'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 8, fontSize: 13, color: '#6b7280' }}>
                {filteredBalances.length} employee(s)
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Requests Tab ──────────────────────────────────────────── */}
      {tab === 'requests' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap' }}>
            {['all', 'pending', 'approved', 'rejected', 'cancelled'].map(s => (
              <button
                key={s}
                onClick={() => setReqFilter(s)}
                style={{
                  padding: '4px 14px',
                  fontSize: 13,
                  borderRadius: 16,
                  border: reqFilter === s ? '2px solid #2563eb' : '1px solid #d1d5db',
                  background: reqFilter === s ? '#eff6ff' : 'white',
                  fontWeight: reqFilter === s ? 700 : 400,
                  cursor: 'pointer',
                  textTransform: 'capitalize' as const,
                }}
              >
                {s}
              </button>
            ))}
          </div>

          {requestsQ.isLoading ? (
            <p style={{ color: '#6b7280' }}>Loading requests...</p>
          ) : requestsQ.error ? (
            <p style={{ color: '#ef4444' }}>Failed to load requests.</p>
          ) : filteredRequests.length === 0 ? (
            <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No leave requests found.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                    <th style={{ padding: '0.75rem' }}>Employee</th>
                    <th style={{ padding: '0.75rem' }}>Type</th>
                    <th style={{ padding: '0.75rem' }}>Dates</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Days</th>
                    <th style={{ padding: '0.75rem' }}>Reason</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>Attachments</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>Status</th>
                    <th style={{ padding: '0.75rem' }}>Approval Stage</th>
                    {canReviewRequests && <th style={{ padding: '0.75rem' }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.map(r => {
                    const normalizedStatus = (r.Status || '').toLowerCase();
                    const displayName = (r.DisplayName || '').trim() || r.EmployeeUpn || 'Unknown employee';
                    const canTakeAction = !!r.CanReview && normalizedStatus === 'pending';
                    return (
                    <React.Fragment key={r.RequestId}>
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.75rem', fontWeight: 500 }}>{displayName}</td>
                        <td style={{ padding: '0.75rem', color: '#6b7280' }}>
                          {leaveTypes.find(t => t.value === r.LeaveType)?.label || r.LeaveType}
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          {r.StartDate?.substring(0, 10)} → {r.EndDate?.substring(0, 10)}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>{r.BusinessDays}</td>
                        <td style={{ padding: '0.75rem', color: '#6b7280', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.Reason || '-'}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                            <span style={{ fontSize: 12, color: '#6b7280', minWidth: 18, display: 'inline-block' }}>
                              {r.AttachmentCount || 0}
                            </span>
                            <button
                              type="button"
                              onClick={() => toggleAttachments(r.RequestId)}
                              style={{
                                padding: '4px 10px',
                                borderRadius: 6,
                                border: '1px solid #d1d5db',
                                background: '#fff',
                                color: '#111827',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                              {attachmentViewRequestId === r.RequestId ? 'Hide' : 'View'}
                            </button>
                          </div>
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                          <span style={badgeStyle(normalizedStatus)}>{normalizedStatus || '-'}</span>
                        </td>
                        <td style={{ padding: '0.75rem', color: '#374151', fontWeight: 500 }}>
                          {approvalStageLabel(normalizedStatus)}
                        </td>
                        {canReviewRequests && (
                          <td style={{ padding: '0.75rem' }}>
                            {canTakeAction && (
                              <div style={{ display: 'flex', gap: 4 }}>
                                <button
                                  onClick={() => actionMut.mutate({ id: r.RequestId, status: 'approved' })}
                                  style={{ padding: '4px 10px', fontSize: 12, border: 'none', borderRadius: 4, background: '#10b981', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => actionMut.mutate({ id: r.RequestId, status: 'rejected' })}
                                  style={{ padding: '4px 10px', fontSize: 12, border: 'none', borderRadius: 4, background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
                                >
                                  Reject
                                </button>
                              </div>
                            )}
                            {!canTakeAction && (
                              <span style={{ fontSize: 12, color: '#9ca3af' }}>
                                {r.ReviewedBy ? `by ${r.ReviewedBy.split('@')[0]}` : ''}
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                      {attachmentViewRequestId === r.RequestId && (
                        <tr>
                          <td colSpan={canReviewRequests ? 9 : 8} style={{ padding: '0.75rem', background: '#f9fafb' }}>
                            {attachmentViewLoading ? (
                              <span style={{ color: '#6b7280', fontSize: 12 }}>Loading attachments...</span>
                            ) : attachmentViewRows.length === 0 ? (
                              <span style={{ color: '#6b7280', fontSize: 12 }}>No attachments uploaded.</span>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {attachmentViewRows.map(att => (
                                  <div key={att.AttachmentId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: 12 }}>{att.FileName} ({(att.FileSize / 1024).toFixed(0)} KB)</span>
                                    <button
                                      type="button"
                                      onClick={() => downloadAttachment(att)}
                                      style={{
                                        padding: '3px 10px',
                                        borderRadius: 6,
                                        border: '1px solid #2563eb',
                                        background: '#fff',
                                        color: '#2563eb',
                                        fontWeight: 600,
                                        fontSize: 12,
                                        cursor: 'pointer',
                                      }}
                                    >
                                      Download
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ marginTop: 8, fontSize: 13, color: '#6b7280' }}>
                {filteredRequests.length} request(s)
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Holidays Tab ──────────────────────────────────────────── */}
      {tab === 'holidays' && (
        <>
          {isAdmin && (
            <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Name</label>
                <input
                  type="text"
                  value={newHolName}
                  onChange={e => setNewHolName(e.target.value)}
                  placeholder="Holiday name"
                  style={{ padding: '6px 10px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 4, width: 200 }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Date</label>
                <input
                  type="date"
                  value={newHolDate}
                  onChange={e => setNewHolDate(e.target.value)}
                  style={{ padding: '6px 10px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 4 }}
                />
              </div>
              <button
                disabled={!newHolName.trim() || !newHolDate}
                onClick={() => addHolMut.mutate({ name: newHolName.trim(), holidayDate: newHolDate })}
                style={{ padding: '6px 16px', fontSize: 14, border: 'none', borderRadius: 4, background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600, opacity: (!newHolName.trim() || !newHolDate) ? 0.5 : 1 }}
              >
                Add Holiday
              </button>
            </div>
          )}

          {holidaysQ.isLoading ? (
            <p style={{ color: '#6b7280' }}>Loading holidays...</p>
          ) : holidaysQ.error ? (
            <p style={{ color: '#ef4444' }}>Failed to load holidays.</p>
          ) : !holidaysQ.data?.length ? (
            <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No public holidays for {year}.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', maxWidth: 600, fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                    <th style={{ padding: '0.75rem' }}>Date</th>
                    <th style={{ padding: '0.75rem' }}>Holiday</th>
                    <th style={{ padding: '0.75rem' }}>Day</th>
                    {isAdmin && <th style={{ padding: '0.75rem' }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {holidaysQ.data.map(h => {
                    const d = new Date(h.HolidayDate + 'T00:00:00');
                    const dayName = d.toLocaleDateString('en-ZA', { weekday: 'long' });
                    const past = d < new Date(new Date().toDateString());
                    return (
                      <tr key={h.HolidayId} style={{ borderBottom: '1px solid #e5e7eb', opacity: past ? 0.5 : 1 }}>
                        <td style={{ padding: '0.75rem' }}>{h.HolidayDate}</td>
                        <td style={{ padding: '0.75rem', fontWeight: 500 }}>{h.Name}</td>
                        <td style={{ padding: '0.75rem', color: '#6b7280' }}>{dayName}</td>
                        {isAdmin && (
                          <td style={{ padding: '0.75rem' }}>
                            <button
                              onClick={() => delHolMut.mutate(h.HolidayId)}
                              style={{ padding: '2px 8px', fontSize: 12, border: '1px solid #fca5a5', borderRadius: 4, background: '#fff1f2', color: '#ef4444', cursor: 'pointer' }}
                            >
                              Remove
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Balance edit modal ────────────────────────────────────── */}
      {editingUpn && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', color: '#111', padding: '2rem', borderRadius: 8, maxWidth: 420, width: '95%' }}>
            <h3 style={{ marginTop: 0 }}>Edit Leave Balance</h3>
            <p style={{ color: '#6b7280', fontSize: 13, marginBottom: '1rem' }}>{editingUpn}</p>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Annual Days (entitlement)</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={editAnnual}
                  onChange={e => setEditAnnual(e.target.value === '' ? '' : Number(e.target.value))}
                  style={{ width: '100%', padding: '6px 10px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 4 }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Adjustment (+/-)</label>
                <input
                  type="number"
                  step="0.5"
                  value={editAdj}
                  onChange={e => setEditAdj(e.target.value === '' ? '' : Number(e.target.value))}
                  style={{ width: '100%', padding: '6px 10px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 4 }}
                />
                <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                  Use to carry over days from previous year, or deduct for early usage.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button
                onClick={() => setEditingUpn(null)}
                style={{ padding: '8px 16px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 4, background: 'white', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={saveBalance}
                style={{ padding: '8px 16px', fontSize: 14, border: 'none', borderRadius: 4, background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveManagementPage;
